import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Clock, Users, Briefcase, Plus, X, Trash2, Calendar, AlertTriangle, Loader2, BarChart3, TrendingUp, Pencil, Save, Filter, UserPlus } from "lucide-react";
import { toast } from "sonner";

// ─── Catálogo TIPO/FASE → PAQUETE_TRABAJO (replicado del backend) ──────────
const TIPO_FASE_OPCIONES = [
  "CONSULTA MERCADO",
  "ESTRUCTURA DE COSTES",
  "ESTUDIO PREVIO",
  "LICITACIÓN",
  "RECURSO",
  "OFICINA TÉCNICA",
];

const PAQUETES_POR_FASE = {
  "CONSULTA MERCADO":     [],
  "ESTRUCTURA DE COSTES": [],
  "ESTUDIO PREVIO": [
    "ESTUDIO DE CAMPO",
    "ESTUDIO ECONOMICO PREVIO",
    "ANÁLISIS PROVEEDORES",
  ],
  "LICITACIÓN": [
    "ANÁLISIS DE PLIEGOS",
    "DIMENSIONAMIENTO DEL SERVICIO",
    "ESTUDIO ECONÓMICO",
    "CRITERIO: DEFINICIÓN DEL SERVICIO",
    "CRITERIO: IMPLANTACIÓN",
    "CRITERIO: MEDIOS MATERIALES",
    "CRITERIO: SISTEMAS/APLICACIONES INFORMÁTICAS",
    "CRITERIO: CAMPAÑA DE INFORMACIÓN",
    "CRITERIO: OTROS ESPECÍFICOS DE LA LICITACIÓN",
    "INSTALACIONES: MEMORIA",
    "INSTALACIONES: MEMORIA TÉCNICA",
    "INSTALACIONES: ANTEPROYECTO NAVE",
    "INSTALACIONES: PROYECTO BÁSICO NAVE",
    "INSTALACIONES: SOTERRADOS",
    "INSTALACIONES: PUNTO LIMPIO",
    "INSTALACIONES: ADECUACIÓN CANTONES",
  ],
  "RECURSO": [],
  "OFICINA TÉCNICA": [
    "MODIFICADO",
    "PROYECTO DE IMPLANTACIÓN",
    "REDACCIÓN ANTEPROYECTO",
    "COORDINACIÓN INFORME GEOTÉCNICO",
    "COORDINACIÓN LEVANTAMIENTO TOPOGRÁFICO",
    "COORDINACIÓN PROYECTO BÁSICO",
    "COORDINACIÓN PROYECTO DE EJECUCIÓN",
    "ANÁLISIS PROVEEDORES",
    "APOYO A LA GESTIÓN",
  ],
};

const ESTADO_LABEL = {
  "analizando":  "EN PLANIFICACIÓN / ANALIZANDO",
  "en_estudio":  "EN ESTUDIO",
  "presentado":  "PRESENTADO",
  "en_curso":    "EN CURSO",
  "finalizado":  "FINALIZADO",
};

export default function GestionHoras() {
  const [tab, setTab] = useState("proyectos");
  return (
    <div className="space-y-5 w-full" data-testid="horas-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Gestión de Horas</h1>
        <p className="text-sm text-gray-500 mt-0.5">Proyectos, imputaciones y control legal de jornada (40h/semana · 1.760h/año)</p>
      </div>

      {/* Iter313 · O01 — esta cartera de "Proyectos" (imputación de horas a
          fases de licitación/comercial) es un catálogo independiente del
          de "Proyectos" en Ejecución/IT (sincronizado con Azure DevOps).
          No es un error que los recuentos no coincidan. */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800" data-testid="horas-scope-note">
        <Briefcase size={14} className="flex-shrink-0 mt-0.5" />
        <span>
          Esta cartera de <b>Proyectos</b> registra fases comerciales/licitaciones para imputar horas (control de jornada).
          Es un catálogo distinto del de <b>Proyectos IT</b> (Ejecución → Proyectos, sincronizado con Azure DevOps) — por diseño no comparten población ni deben fusionarse.
        </span>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {[
          { key: "proyectos",    icon: Briefcase,    label: "Proyectos" },
          { key: "imputaciones", icon: Clock,        label: "Imputaciones" },
          { key: "equipo",       icon: Users,        label: "Equipo" },
          { key: "detalle",      icon: BarChart3,    label: "Detalle proyecto" },
          { key: "colaboradores",icon: TrendingUp,   label: "Dashboard colab." },
        ].map(m => {
          const Icon = m.icon; const active = tab === m.key;
          return (
            <button key={m.key} onClick={() => setTab(m.key)} data-testid={`h-tab-${m.key}`}
                    className={`rounded-xl border p-3 text-left transition-all ${active ? "bg-[#007934] text-white border-[#007934] shadow-md" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200"}`}>
              <div className="flex items-center gap-2"><Icon size={14}/><span className="text-sm font-semibold">{m.label}</span></div>
            </button>
          );
        })}
      </div>

      {tab === "proyectos"     && <Proyectos/>}
      {tab === "imputaciones"  && <Imputaciones/>}
      {tab === "equipo"        && <Equipo/>}
      {tab === "detalle"       && <DetalleProyecto/>}
      {tab === "colaboradores" && <DashboardColab/>}
    </div>
  );
}

function Proyectos() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);  // proyecto siendo editado
  const [filters, setFilters] = useState({ q: "", cliente: "", responsable: "", tipo_fase: "", estado: "", fecha_inicio_from: "", fecha_inicio_to: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/horas/proyectos${qs?`?${qs}`:""}`);
      setData(r.data); setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudieron cargar los proyectos. Reintenta.");
    }
  };
  useEffect(() => { load(); }, [qs]); // eslint-disable-line
  const del = async (id) => {
    if (!window.confirm("Eliminar proyecto (y todas sus imputaciones)?")) return;
    await axios.delete(`${API}/horas/proyectos/${id}`); load();
  };
  const resetFilters = () => setFilters({ q: "", cliente: "", responsable: "", tipo_fase: "", estado: "", fecha_inicio_from: "", fecha_inicio_to: "" });
  const activeFilters = Object.values(filters).filter(Boolean).length;

  if (error) return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between" data-testid="proyectos-error">
      <span>⚠ {error}</span>
      <button onClick={load} className="font-semibold underline" data-testid="proyectos-retry-btn">Reintentar</button>
    </div>
  );
  if (!data) return <div className="text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-3"><div className="text-[10px] uppercase text-gray-500">Total proyectos</div><div className="text-lg font-bold">{data.kpis.total}</div></div>
        <div className="bg-white rounded-xl border border-gray-200 p-3"><div className="text-[10px] uppercase text-gray-500">Activos</div><div className="text-lg font-bold">{data.kpis.activos}</div></div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          placeholder="Buscar: código, nombre, cliente, responsable…"
          value={filters.q}
          onChange={e=>setFilters({...filters, q: e.target.value})}
          className="flex-1 min-w-[220px] border rounded-lg px-3 py-1.5 text-xs"
          data-testid="proy-filter-q"
        />
        <button
          onClick={()=>setFiltersOpen(v=>!v)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 border ${activeFilters>0?"bg-indigo-50 border-indigo-200 text-indigo-700":"bg-white border-gray-200 text-gray-600"}`}
          data-testid="proy-filter-toggle">
          <Filter size={12}/> Filtros {activeFilters>0 && <span className="bg-indigo-600 text-white text-[10px] px-1.5 rounded-full">{activeFilters}</span>}
        </button>
        {activeFilters>0 && <button onClick={resetFilters} className="text-xs text-gray-500 hover:text-gray-700" data-testid="proy-filter-reset">Limpiar</button>}
        <button onClick={()=>setShowForm(true)} className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="proyecto-add-btn">
          <Plus size={13}/> Nuevo proyecto
        </button>
      </div>

      {filtersOpen && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="proy-filter-panel">
          <L label="Cliente"><input value={filters.cliente} onChange={e=>setFilters({...filters,cliente:e.target.value})} className="w-full border rounded px-2 py-1" data-testid="proy-filter-cliente"/></L>
          <L label="Responsable"><input value={filters.responsable} onChange={e=>setFilters({...filters,responsable:e.target.value})} className="w-full border rounded px-2 py-1" data-testid="proy-filter-responsable"/></L>
          <L label="Tipo / Fase">
            <select value={filters.tipo_fase} onChange={e=>setFilters({...filters,tipo_fase:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="proy-filter-tipofase">
              <option value="">(todos)</option>
              {TIPO_FASE_OPCIONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </L>
          <L label="Estado">
            <select value={filters.estado} onChange={e=>setFilters({...filters,estado:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="proy-filter-estado">
              <option value="">(todos)</option>
              {Object.entries(ESTADO_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </L>
          <L label="F. Inicio desde"><input type="date" value={filters.fecha_inicio_from} onChange={e=>setFilters({...filters,fecha_inicio_from:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="F. Inicio hasta"><input type="date" value={filters.fecha_inicio_to} onChange={e=>setFilters({...filters,fecha_inicio_to:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-600"><tr>
            <th className="text-left p-2.5">Código</th><th className="text-left p-2.5">Nombre</th><th className="text-left p-2.5">Cliente</th>
            <th className="text-left p-2.5">Responsable</th>
            <th className="text-left p-2.5">Tipo / Fase</th>
            <th className="text-left p-2.5">Estado</th>
            <th className="text-left p-2.5">F. Inicio</th>
            <th className="text-center p-2.5"></th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {(data.items || []).map(p => (
              <tr key={p.id} className="hover:bg-gray-50" data-testid={`proyecto-row-${p.id}`}>
                <td className="p-2.5 font-mono text-[10px]">{p.codigo_proyecto}</td>
                <td className="p-2.5 font-semibold">{p.nombre}</td>
                <td className="p-2.5">{p.cliente}</td>
                <td className="p-2.5">{p.responsable}</td>
                <td className="p-2.5">
                  {p.tipo_fase ? (
                    <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">{p.tipo_fase}</span>
                  ) : <span className="text-[10px] text-gray-300">—</span>}
                </td>
                <td className="p-2.5"><span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-semibold">{ESTADO_LABEL[p.estado] || p.estado}</span></td>
                <td className="p-2.5 text-gray-500">{p.fecha_inicio || "—"}</td>
                <td className="p-2.5 text-center">
                  <button onClick={()=>setEditing(p)} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded mr-1" data-testid={`proyecto-edit-${p.id}`} title="Editar"><Pencil size={11}/></button>
                  <button onClick={()=>del(p.id)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" title="Eliminar"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
            {!data.items?.length && <tr><td colSpan={8} className="p-6 text-center text-gray-400">Sin proyectos{activeFilters>0?" (revisa filtros)":""}</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <ProyectoForm onClose={()=>{setShowForm(false);load();}}/>}
      {editing && <ProyectoForm initial={editing} onClose={()=>{setEditing(null);load();}}/>}
    </div>
  );
}

function ProyectoForm({ initial, onClose }) {
  const isEdit = !!initial;
  const [f, setF] = useState(initial ? {
    codigo_proyecto: initial.codigo_proyecto || "",
    nombre: initial.nombre || "",
    cliente: initial.cliente || "",
    responsable: initial.responsable || "",
    estado: initial.estado || "analizando",
    actividad: initial.actividad || "",
    tipo_fase: initial.tipo_fase || "",
    fecha_inicio: initial.fecha_inicio || "",
    fecha_vencimiento: initial.fecha_vencimiento || "",
    descripcion: initial.descripcion || "",
  } : {
    codigo_proyecto: "", nombre: "", cliente: "", responsable: "",
    estado: "analizando", actividad: "", tipo_fase: "",
    fecha_inicio: "", fecha_vencimiento: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.codigo_proyecto || !f.nombre) { toast.error("Código y nombre requeridos"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/horas/proyectos/${initial.id}`, f);
        toast.success("Proyecto actualizado");
      } else {
        await axios.post(`${API}/horas/proyectos`, f);
        toast.success("Creado");
      }
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">{isEdit ? "Editar proyecto" : "Nuevo proyecto"}</h3>
          <button onClick={onClose} className="text-gray-400"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <L label="Código *"><input value={f.codigo_proyecto} onChange={e=>setF({...f,codigo_proyecto:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Estado">
            <select value={f.estado} onChange={e=>setF({...f,estado:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="proyecto-estado">
              <option value="analizando">EN PLANIFICACIÓN / ANALIZANDO</option>
              <option value="en_estudio">EN ESTUDIO</option>
              <option value="presentado">PRESENTADO</option>
              <option value="en_curso">EN CURSO</option>
              <option value="finalizado">FINALIZADO</option>
            </select>
          </L>
          <L label="Nombre *" span={2}><input value={f.nombre} onChange={e=>setF({...f,nombre:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Cliente"><input value={f.cliente} onChange={e=>setF({...f,cliente:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Responsable"><input value={f.responsable} onChange={e=>setF({...f,responsable:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Tipo / Fase de proyecto" span={2}>
            <select
              value={f.tipo_fase || ""}
              onChange={e => setF({...f, tipo_fase: e.target.value})}
              className="w-full border rounded px-2 py-1 bg-white"
              data-testid="proyecto-tipo-fase">
              <option value="">— Selecciona —</option>
              {TIPO_FASE_OPCIONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">
              Define qué paquetes de trabajo aparecerán al imputar horas a este proyecto.
            </p>
          </L>
          <L label="F. inicio"><input type="date" value={f.fecha_inicio} onChange={e=>setF({...f,fecha_inicio:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="F. vencimiento"><input type="date" value={f.fecha_vencimiento} onChange={e=>setF({...f,fecha_vencimiento:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid="proyecto-save-btn">
            {saving?<><Loader2 size={11} className="animate-spin"/>Guardando…</>:<><Save size={11}/>{isEdit?"Guardar":"Crear"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Imputaciones() {
  const [imps, setImps] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ proyecto_id: "", colaborador_email: "", paquete_trabajo: "", tipo_fase: "", start: "", end: "", q: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const load = async () => {
    try {
      const [i, p, u] = await Promise.all([
        axios.get(`${API}/horas/imputaciones${qs?`?${qs}`:""}`),
        axios.get(`${API}/horas/proyectos`),
        axios.get(`${API}/horas/users`),
      ]);
      setImps(i.data); setProyectos(p.data.items || []); setUsers(u.data); setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudieron cargar las imputaciones. Reintenta.");
    }
  };
  useEffect(() => { load(); }, [qs]); // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm("¿Eliminar imputación?")) return;
    await axios.delete(`${API}/horas/imputaciones/${id}`); load();
  };

  const proyNombre = (id) => proyectos.find(p => p.id === id)?.nombre || "—";
  const resetFilters = () => setFilters({ proyecto_id: "", colaborador_email: "", paquete_trabajo: "", tipo_fase: "", start: "", end: "", q: "" });
  const activeFilters = Object.values(filters).filter(Boolean).length;

  // Lista dinámica de paquetes según tipo_fase (si se filtra por fase) o todos los distintos presentes
  const allPaquetes = useMemo(() => {
    const s = new Set();
    if (filters.tipo_fase) {
      (PAQUETES_POR_FASE[filters.tipo_fase] || []).forEach(p => s.add(p));
    } else {
      Object.values(PAQUETES_POR_FASE).flat().forEach(p => s.add(p));
    }
    return [...s].sort();
  }, [filters.tipo_fase]);

  return (
    <div className="space-y-4" data-testid="imputaciones">
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between" data-testid="imputaciones-error">
          <span>⚠ {error}</span>
          <button onClick={load} className="font-semibold underline" data-testid="imputaciones-retry-btn">Reintentar</button>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          placeholder="Buscar: descripción, tarea, colaborador…"
          value={filters.q}
          onChange={e=>setFilters({...filters, q: e.target.value})}
          className="flex-1 min-w-[220px] border rounded-lg px-3 py-1.5 text-xs"
          data-testid="imp-filter-q"
        />
        <button
          onClick={()=>setFiltersOpen(v=>!v)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 border ${activeFilters>0?"bg-indigo-50 border-indigo-200 text-indigo-700":"bg-white border-gray-200 text-gray-600"}`}
          data-testid="imp-filter-toggle">
          <Filter size={12}/> Filtros {activeFilters>0 && <span className="bg-indigo-600 text-white text-[10px] px-1.5 rounded-full">{activeFilters}</span>}
        </button>
        {activeFilters>0 && <button onClick={resetFilters} className="text-xs text-gray-500 hover:text-gray-700" data-testid="imp-filter-reset">Limpiar</button>}
        <button onClick={()=>setShowForm(true)} className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="imp-add-btn">
          <Plus size={13}/> Nueva imputación
        </button>
      </div>

      {filtersOpen && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="imp-filter-panel">
          <L label="Proyecto">
            <select value={filters.proyecto_id} onChange={e=>setFilters({...filters,proyecto_id:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="imp-filter-proy">
              <option value="">(todos)</option>
              {proyectos.map(p => <option key={p.id} value={p.id}>{p.codigo_proyecto} · {p.nombre}</option>)}
            </select>
          </L>
          <L label="Colaborador">
            <select value={filters.colaborador_email} onChange={e=>setFilters({...filters,colaborador_email:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="imp-filter-colab">
              <option value="">(todos)</option>
              {users.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
            </select>
          </L>
          <L label="Tipo / Fase (proyecto)">
            <select value={filters.tipo_fase} onChange={e=>setFilters({...filters,tipo_fase:e.target.value, paquete_trabajo: ""})} className="w-full border rounded px-2 py-1 bg-white" data-testid="imp-filter-tipofase">
              <option value="">(todos)</option>
              {TIPO_FASE_OPCIONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </L>
          <L label="Paquete de trabajo">
            <select value={filters.paquete_trabajo} onChange={e=>setFilters({...filters,paquete_trabajo:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="imp-filter-paquete">
              <option value="">(todos)</option>
              {allPaquetes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </L>
          <L label="Fecha desde"><input type="date" value={filters.start} onChange={e=>setFilters({...filters,start:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Fecha hasta"><input type="date" value={filters.end} onChange={e=>setFilters({...filters,end:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-600"><tr>
            <th className="text-left p-2.5">Fecha</th><th className="text-center p-2.5">Sem.</th><th className="text-left p-2.5">Colaborador</th>
            <th className="text-left p-2.5">Proyecto</th>
            <th className="text-left p-2.5">Paquete trabajo</th>
            <th className="text-left p-2.5">Tarea</th>
            <th className="text-right p-2.5">Horas</th><th className="text-left p-2.5">Descripción</th>
            <th className="text-center p-2.5"></th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {imps.slice(0, 500).map(i => (
              <tr key={i.id} className="hover:bg-gray-50">
                <td className="p-2.5">{i.fecha}</td>
                <td className="p-2.5 text-center text-gray-500">W{i.iso_week || "?"}</td>
                <td className="p-2.5 font-semibold">{i.colaborador_nombre || i.colaborador_email}</td>
                <td className="p-2.5">{proyNombre(i.proyecto_id)}</td>
                <td className="p-2.5">
                  {i.paquete_trabajo ? (
                    <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">{i.paquete_trabajo}</span>
                  ) : <span className="text-[10px] text-gray-300">—</span>}
                </td>
                <td className="p-2.5 text-[11px]">
                  {i.tarea ? <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">{i.tarea}</span> : <span className="text-gray-300">—</span>}
                </td>
                <td className="p-2.5 text-right font-mono font-bold text-[#007934]">{i.horas}</td>
                <td className="p-2.5 text-gray-600 max-w-[300px] truncate">{i.descripcion}</td>
                <td className="p-2.5 text-center">
                  <button onClick={()=>setEditing(i)} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded mr-1" data-testid={`imp-edit-${i.id}`} title="Editar"><Pencil size={11}/></button>
                  <button onClick={()=>del(i.id)} className="text-rose-500 p-1" title="Eliminar"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
            {!imps.length && <tr><td colSpan={9} className="p-6 text-center text-gray-400">Sin imputaciones{activeFilters>0?" (revisa filtros)":" — crea la primera"}</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <ImpForm proyectos={proyectos} users={users} onClose={()=>{setShowForm(false);load();}}/>}
      {editing && <ImpForm proyectos={proyectos} users={users} initial={editing} onClose={()=>{setEditing(null);load();}}/>}
    </div>
  );
}

function ImpForm({ proyectos, users, initial, onClose }) {
  const isEdit = !!initial;
  // Iter155 · Estado local para colaboradores externos frescos añadidos sin
  // recargar la lista completa. `usersState` refleja users + nuevos externos.
  const [usersState, setUsersState] = useState(users);
  useEffect(() => { setUsersState(users); }, [users]);
  const [showNewCollab, setShowNewCollab] = useState(false);
  const [f, setF] = useState(initial ? {
    proyecto_id: initial.proyecto_id || "",
    colaborador_email: initial.colaborador_email || "",
    paquete_trabajo: initial.paquete_trabajo || "",
    tarea: initial.tarea || "",
    fecha: initial.fecha || new Date().toISOString().slice(0,10),
    horas: initial.horas || 8,
    descripcion: initial.descripcion || "",
  } : {
    proyecto_id: proyectos[0]?.id || "", colaborador_email: users[0]?.email || "",
    paquete_trabajo: "", tarea: "",
    fecha: new Date().toISOString().slice(0,10), horas: 8, descripcion: "",
  });
  const [saving, setSaving] = useState(false);

  // Iter155 · alta rápida de colaborador externo
  const handleCollabCreated = (nuevo) => {
    setUsersState(prev => [...prev, { ...nuevo, kind: "external" }]);
    setF(prev => ({ ...prev, colaborador_email: nuevo.email }));
    setShowNewCollab(false);
    toast.success(`Colaborador "${nuevo.name}" añadido`);
  };

  // Determinar paquetes disponibles según tipo_fase del proyecto seleccionado
  const proyectoSel = proyectos.find(p => p.id === f.proyecto_id);
  const paquetesDisponibles = proyectoSel?.tipo_fase
    ? (PAQUETES_POR_FASE[proyectoSel.tipo_fase] || [])
    : [];

  // Reset paquete_trabajo si cambia el proyecto (excepto en edit inicial)
  const [firstRun, setFirstRun] = useState(true);
  useEffect(() => {
    if (firstRun) { setFirstRun(false); return; }
    if (paquetesDisponibles.length > 0 && f.paquete_trabajo &&
        !paquetesDisponibles.includes(f.paquete_trabajo)) {
      setF(prev => ({...prev, paquete_trabajo: ""}));
    } else if (paquetesDisponibles.length === 0 && f.paquete_trabajo) {
      setF(prev => ({...prev, paquete_trabajo: ""}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.proyecto_id]);
  const save = async () => {
    if (!f.proyecto_id || !f.colaborador_email || !f.horas) { toast.error("Completa los campos requeridos"); return; }
    setSaving(true);
    try {
      const user = usersState.find(u => u.email === f.colaborador_email);
      const payload = {
        ...f, horas: parseFloat(f.horas),
        paquete_trabajo: f.paquete_trabajo || "",
        tarea: f.tarea || "",
        colaborador_nombre: user?.name || f.colaborador_email,
      };
      if (isEdit) {
        await axios.put(`${API}/horas/imputaciones/${initial.id}`, payload);
        toast.success("Imputación actualizada");
      } else {
        await axios.post(`${API}/horas/imputaciones`, payload);
        toast.success("Imputado");
      }
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-bold">{isEdit?"Editar imputación":"Nueva imputación"}</h3><button onClick={onClose}><X size={16}/></button></div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <L label="Proyecto *" span={2}>
            <select value={f.proyecto_id} onChange={e=>setF({...f,proyecto_id:e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="imp-proyecto">
              <option value="">— Elegir —</option>
              {proyectos.map(p => <option key={p.id} value={p.id}>{p.codigo_proyecto} · {p.nombre}{p.tipo_fase ? ` · ${p.tipo_fase}` : ""}</option>)}
            </select>
          </L>
          {proyectoSel?.tipo_fase && (
            <L label={`Paquete de trabajo · ${proyectoSel.tipo_fase}`} span={2}>
              {paquetesDisponibles.length > 0 ? (
                <select
                  value={f.paquete_trabajo}
                  onChange={e => setF({...f, paquete_trabajo: e.target.value})}
                  className="w-full border rounded px-2 py-1 bg-white"
                  data-testid="imp-paquete-trabajo">
                  <option value="">— Sin paquete específico —</option>
                  {paquetesDisponibles.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <p className="text-[10px] text-gray-500 italic px-2 py-1.5 bg-gray-50 rounded">
                  La fase <strong>{proyectoSel.tipo_fase}</strong> no tiene paquetes de trabajo definidos. Imputa horas directamente al proyecto.
                </p>
              )}
            </L>
          )}
          <L label="Tarea (concreta)" span={2}>
            <input
              value={f.tarea}
              onChange={e => setF({...f, tarea: e.target.value})}
              placeholder="Ej: Dimensionamiento, Memoria Limpieza, Mediciones…"
              className="w-full border rounded px-2 py-1"
              data-testid="imp-tarea"
            />
            <p className="text-[10px] text-gray-400 mt-1">Texto libre. Permite agrupar horas por tarea concreta dentro del paquete.</p>
          </L>
          <L label="Colaborador *" span={2}>
            {/* Iter155 · Users platform + colaboradores externos, con opción de alta rápida. */}
            <div className="flex gap-1.5">
              <select
                value={f.colaborador_email}
                onChange={e=>setF({...f,colaborador_email:e.target.value})}
                className="flex-1 border rounded px-2 py-1 bg-white"
                data-testid="imp-colab">
                {(() => {
                  const platform = usersState.filter(u => u.kind !== "external");
                  const externos = usersState.filter(u => u.kind === "external");
                  return (
                    <>
                      {platform.length > 0 && (
                        <optgroup label="Usuarios plataforma">
                          {platform.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
                        </optgroup>
                      )}
                      {externos.length > 0 && (
                        <optgroup label="Colaboradores externos">
                          {externos.map(u => <option key={u.email} value={u.email}>{u.name || u.email}{u.area ? ` · ${u.area}` : ""}</option>)}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCollab(true)}
                className="text-[10px] font-semibold px-2 py-1 rounded border border-[#007934] text-[#007934] hover:bg-[#007934] hover:text-white inline-flex items-center gap-1 transition"
                data-testid="imp-new-colab-btn"
                title="Dar de alta un colaborador externo (sin acceso a la plataforma)">
                <UserPlus size={11}/> Nuevo
              </button>
            </div>
          </L>
          <L label="Fecha *"><input type="date" value={f.fecha} onChange={e=>setF({...f,fecha:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Horas *"><input type="number" step="0.25" value={f.horas} onChange={e=>setF({...f,horas:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Descripción" span={2}><textarea rows={2} value={f.descripcion} onChange={e=>setF({...f,descripcion:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded" data-testid="imp-save-btn">{saving?"…":(isEdit?"Guardar":"Imputar")}</button>
        </div>
      </div>
      {showNewCollab && (
        <NewColaboradorModal
          onClose={() => setShowNewCollab(false)}
          onCreated={handleCollabCreated}
        />
      )}
    </div>
  );
}

// Iter155 · Modal alta rápida de colaborador externo (sin acceso a plataforma).
function NewColaboradorModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", area: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/horas/colaboradores`, {
        name: f.name.trim(),
        email: f.email.trim(),
        area: f.area.trim(),
        activo: true,
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el colaborador");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="new-colab-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold inline-flex items-center gap-2"><UserPlus size={14} className="text-[#007934]"/>Nuevo colaborador externo</h3>
          <button onClick={onClose} data-testid="new-colab-close"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <L label="Nombre *" span={2}>
            <input
              value={f.name}
              onChange={e=>setF({...f,name:e.target.value})}
              className="w-full border rounded px-2 py-1"
              placeholder="Ej: Juan García"
              autoFocus
              data-testid="new-colab-name"/>
          </L>
          <L label="Email (opcional)" span={2}>
            <input
              value={f.email}
              onChange={e=>setF({...f,email:e.target.value})}
              className="w-full border rounded px-2 py-1"
              placeholder="Si es interno o quieres poder identificarlo"
              type="email"
              data-testid="new-colab-email"/>
            <p className="text-[10px] text-gray-400 mt-1">Sin email se generará uno interno. No dará acceso a la plataforma.</p>
          </L>
          <L label="Área / Empresa" span={2}>
            <input
              value={f.area}
              onChange={e=>setF({...f,area:e.target.value})}
              className="w-full border rounded px-2 py-1"
              placeholder="Ej: Ingeniería · Freelance · Subcontrata"
              data-testid="new-colab-area"/>
          </L>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded inline-flex items-center gap-1"
            data-testid="new-colab-save">
            {saving ? <><Loader2 size={11} className="animate-spin"/>Creando…</> : <>Crear colaborador</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardColab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const loadColab = () => {
    setError("");
    axios.get(`${API}/horas/dashboard/colaboradores?year=${year}`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || "No se pudo cargar el dashboard de colaboradores. Reintenta."));
  };
  useEffect(() => { loadColab(); }, [year]); // eslint-disable-line

  if (error) return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between" data-testid="dashboard-colab-error">
      <span>⚠ {error}</span>
      <button onClick={loadColab} className="font-semibold underline" data-testid="dashboard-colab-retry-btn">Reintentar</button>
    </div>
  );
  if (!data) return <div className="text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando…</div>;

  const ALERT = { ok: "bg-green-100 text-green-700", warning: "bg-amber-100 text-amber-700", danger: "bg-rose-100 text-rose-700" };

  return (
    <div className="space-y-4" data-testid="dashboard-colab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-gray-600 font-semibold">Año:</label>
        <input type="number" value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border rounded px-3 py-1.5 text-xs w-24"/>
        <span className="text-xs text-gray-500">Máximo: {data.config.max_anual}h/año · {data.config.max_semanal}h/semana</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-600"><tr>
            <th className="text-left p-2.5">Colaborador</th>
            <th className="text-right p-2.5">Horas YTD</th>
            <th className="text-right p-2.5">% anual</th>
            <th className="text-right p-2.5">Sem. actual</th>
            <th className="text-right p-2.5">% semana</th>
            <th className="text-left p-2.5">Top 3 proyectos</th>
            <th className="text-center p-2.5">Sem. imput.</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {(data.colaboradores || []).map(c => (
              <tr key={c.email} className="hover:bg-gray-50" data-testid={`colab-row-${c.email}`}>
                <td className="p-2.5 font-semibold">{c.nombre}<div className="text-[10px] text-gray-400">{c.email}</div></td>
                <td className="p-2.5 text-right font-mono font-bold">{c.horas_ytd}</td>
                <td className="p-2.5 text-right">
                  <span className={`inline-block px-2 py-0.5 rounded font-bold ${ALERT[c.alert_anual]}`}>{c.pct_anual}%</span>
                </td>
                <td className="p-2.5 text-right font-mono">{c.horas_semana_actual}</td>
                <td className="p-2.5 text-right">
                  <span className={`inline-block px-2 py-0.5 rounded font-bold ${ALERT[c.alert_semanal]}`}>{c.pct_semana}%</span>
                </td>
                <td className="p-2.5">
                  <div className="space-y-0.5">
                    {c.top_proyectos.map((p, i) => (
                      <div key={i} className="text-[10px] truncate max-w-[200px]">{p.nombre} · <span className="font-mono font-bold">{p.horas}h</span></div>
                    ))}
                  </div>
                </td>
                <td className="p-2.5 text-center text-[10px] text-gray-500">{c.semanas_imputadas}</td>
              </tr>
            ))}
            {!data.colaboradores?.length && <tr><td colSpan={7} className="p-6 text-center text-gray-400">Sin imputaciones este año — crea alguna desde la pestaña &quot;Imputaciones&quot;</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Equipo() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [filters, setFilters] = useState({ colaborador_email: "", proyecto_id: "", tipo_fase: "" });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.colaborador_email) params.append("colaborador_email", filters.colaborador_email);
      if (filters.proyecto_id) params.append("proyecto_id", filters.proyecto_id);
      if (filters.tipo_fase) params.append("tipo_fase", filters.tipo_fase);
      const r = await axios.get(`${API}/horas/equipo?${params}`);
      setData(r.data); setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cargar la vista de equipo. Reintenta.");
    }
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/horas/users`),
      axios.get(`${API}/horas/proyectos`),
    ]).then(([u, p]) => { setUsers(u.data); setProyectos(p.data.items || []); })
      .catch(err => setError(err.response?.data?.detail || "No se pudieron cargar los filtros de equipo."));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  if (error) return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between" data-testid="equipo-error">
      <span>⚠ {error}</span>
      <button onClick={load} className="font-semibold underline" data-testid="equipo-retry-btn">Reintentar</button>
    </div>
  );
  if (!data) return <div className="text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="equipo-tab">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2">
        <KpiCard label="Filas" value={data.totals.n_filas}/>
        <KpiCard label="Horas total" value={data.totals.horas_total} suffix="h"/>
        <KpiCard label="Colaboradores" value={data.totals.colaboradores}/>
        <KpiCard label="Proyectos" value={data.totals.proyectos}/>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <select value={filters.colaborador_email} onChange={e=>setFilters(f=>({...f,colaborador_email:e.target.value}))}
                className="text-xs border rounded px-2 py-1 bg-white" data-testid="equipo-filter-colab">
          <option value="">Todos los colaboradores</option>
          {users.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
        </select>
        <select value={filters.proyecto_id} onChange={e=>setFilters(f=>({...f,proyecto_id:e.target.value}))}
                className="text-xs border rounded px-2 py-1 bg-white" data-testid="equipo-filter-proy">
          <option value="">Todos los proyectos</option>
          {proyectos.map(p => <option key={p.id} value={p.id}>{p.codigo_proyecto} · {p.nombre}</option>)}
        </select>
        <select value={filters.tipo_fase} onChange={e=>setFilters(f=>({...f,tipo_fase:e.target.value}))}
                className="text-xs border rounded px-2 py-1 bg-white" data-testid="equipo-filter-fase">
          <option value="">Todas las fases</option>
          {TIPO_FASE_OPCIONES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filters.colaborador_email || filters.proyecto_id || filters.tipo_fase) && (
          <button onClick={()=>setFilters({colaborador_email:"",proyecto_id:"",tipo_fase:""})}
                  className="text-[11px] text-gray-500 hover:text-rose-600 underline">Limpiar filtros</button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-600"><tr>
            <th className="text-left p-2.5">Colaborador</th>
            <th className="text-left p-2.5">Fase / Tipo</th>
            <th className="text-left p-2.5">Proyecto</th>
            <th className="text-left p-2.5">Paquete trabajo</th>
            <th className="text-left p-2.5">Tarea</th>
            <th className="text-center p-2.5">F. Inicio</th>
            <th className="text-center p-2.5">F. Fin</th>
            <th className="text-right p-2.5">Horas</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {data.rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50" data-testid={`equipo-row-${i}`}>
                <td className="p-2.5 font-semibold">{r.colaborador_nombre}</td>
                <td className="p-2.5">{r.tipo_fase ? <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold">{r.tipo_fase}</span> : <span className="text-gray-300">—</span>}</td>
                <td className="p-2.5">
                  <span className="font-semibold">{r.proyecto_nombre}</span>
                  {r.proyecto_codigo && <div className="text-[10px] text-gray-400 font-mono">{r.proyecto_codigo}</div>}
                </td>
                <td className="p-2.5">{r.paquete_trabajo ? <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{r.paquete_trabajo}</span> : <span className="text-gray-300">—</span>}</td>
                <td className="p-2.5">{r.tarea ? <span className="text-[11px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">{r.tarea}</span> : <span className="text-gray-300">—</span>}</td>
                <td className="p-2.5 text-center text-gray-500 text-[11px]">{r.fecha_inicio || "—"}</td>
                <td className="p-2.5 text-center text-gray-500 text-[11px]">{r.fecha_fin || "—"}</td>
                <td className="p-2.5 text-right font-mono font-bold text-[#007934]">{r.horas}</td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={8} className="p-6 text-center text-gray-400">Sin imputaciones para los filtros aplicados</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DetalleProyecto() {
  const [proyectos, setProyectos] = useState([]);
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    axios.get(`${API}/horas/proyectos`).then(r => {
      const items = r.data.items || [];
      setProyectos(items);
      if (items.length && !pid) setPid(items[0].id);
    }).catch(err => setError(err.response?.data?.detail || "No se pudo cargar la lista de proyectos."));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!pid) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/horas/proyectos/${pid}/detalle`)
      .then(r => { setData(r.data); setError(""); })
      .catch(err => setError(err.response?.data?.detail || "No se pudo cargar el detalle del proyecto. Reintenta."))
      .finally(() => setLoading(false));
  }, [pid, retryTick]);

  return (
    <div className="space-y-4" data-testid="detalle-proyecto-tab">
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between" data-testid="detalle-proyecto-error">
          <span>⚠ {error}</span>
          <button onClick={() => setRetryTick(t => t + 1)} className="font-semibold underline" data-testid="detalle-proyecto-retry-btn">Reintentar</button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3">
        <label className="text-xs font-semibold text-gray-700">Proyecto:</label>
        <select value={pid} onChange={e=>setPid(e.target.value)}
                className="flex-1 text-xs border rounded px-3 py-1.5 bg-white" data-testid="detalle-proyecto-select">
          <option value="">— Selecciona un proyecto —</option>
          {proyectos.map(p => <option key={p.id} value={p.id}>{p.codigo_proyecto} · {p.nombre}{p.tipo_fase ? ` · ${p.tipo_fase}` : ""}</option>)}
        </select>
      </div>

      {loading && <div className="text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando…</div>}

      {data && !loading && (
        <>
          {/* Cabecera proyecto + KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 p-4 space-y-2">
              <h3 className="text-xs uppercase tracking-wider font-bold text-gray-500">Información del proyecto</h3>
              <Field label="Tipo / Fase Proyecto" value={data.proyecto.tipo_fase || "—"} highlight/>
              <Field label="Nombre del proyecto"  value={data.proyecto.nombre}/>
              <Field label="Cliente"              value={data.proyecto.cliente || "—"}/>
              <Field label="Estado"               value={ESTADO_LABEL[data.proyecto.estado] || data.proyecto.estado || "—"}/>
              <Field label="Responsable"          value={data.proyecto.responsable || "—"}/>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 grid grid-cols-2 gap-2 content-start">
              <h3 className="col-span-2 text-xs uppercase tracking-wider font-bold text-blue-700">Total imputado</h3>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-semibold text-blue-600">Total horas</p>
                <p className="text-2xl font-bold text-blue-900 font-['Outfit']">{data.kpis.total_horas}</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-semibold text-blue-600">Nº colaboradores</p>
                <p className="text-2xl font-bold text-blue-900 font-['Outfit']">{data.kpis.n_colaboradores}</p>
              </div>
              <div className="col-span-2 text-[10px] text-blue-600 text-center">{data.kpis.n_imputaciones} imputaciones registradas</div>
            </div>
          </div>

          {/* Tabla detalle */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-600"><tr>
                <th className="text-left p-2.5">Colaborador</th>
                <th className="text-left p-2.5">Paquete trabajo</th>
                <th className="text-left p-2.5">Tarea</th>
                <th className="text-center p-2.5">Fecha inicio</th>
                <th className="text-center p-2.5">Fecha fin</th>
                <th className="text-right p-2.5">Horas imputadas tarea</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.filas.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="p-2.5 font-semibold">{r.colaborador_nombre}</td>
                    <td className="p-2.5">{r.paquete_trabajo ? <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{r.paquete_trabajo}</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="p-2.5">{r.tarea ? <span className="text-[11px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">{r.tarea}</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="p-2.5 text-center text-gray-500 text-[11px]">{r.fecha_inicio || "—"}</td>
                    <td className="p-2.5 text-center text-gray-500 text-[11px]">{r.fecha_fin || "—"}</td>
                    <td className="p-2.5 text-right font-mono font-bold text-[#007934]">{r.horas}</td>
                  </tr>
                ))}
                {!data.filas.length && <tr><td colSpan={6} className="p-6 text-center text-gray-400">Aún no hay imputaciones en este proyecto</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


function KpiCard({ label, value, suffix = "" }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3">
      <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">{label}</div>
      <div className="text-xl font-bold text-gray-900 font-['Outfit']">{value}{suffix && <span className="text-xs ml-1 text-gray-400">{suffix}</span>}</div>
    </div>
  );
}


function Field({ label, value, highlight = false }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center">
      <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide">{label}</div>
      <div className={`col-span-2 text-sm ${highlight ? "font-bold text-indigo-700" : "text-gray-800"}`}>{value}</div>
    </div>
  );
}


function L({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">{label}</div>
      {children}
    </div>
  );
}
